import { computedFrom } from 'aurelia-framework';
import { isEmpty, keys } from 'lodash';
import {
	IImportAM,
	IImportItemAM,
	IImportMappingErrorAM,
	ImportItemStatus,
	ImportMappingErrorSeverity,
	ImportService
} from './import-api';

export class ImportItem {
	model: IImportItemAM;
	viewModel: IImportItemList;
	previousStatus: ImportItemStatus;
	accountErrors: IImportMappingErrorAM[];
	costCenterErrors: IImportMappingErrorAM[];

	@computedFrom('model.status')
	get canReject() {
		if (this.viewModel.importEntry.status === 'Archived') {
			return false;
		}

		return this.model.status === ImportItemStatus.Staged
			|| this.model.status === ImportItemStatus.Warning
			|| this.model.status === ImportItemStatus.Error;
	}

	@computedFrom('model.status')
	get canUndo() {
		if (this.viewModel.importEntry.status === 'Archived') {
			return false;
		}

		return this.model.status === ImportItemStatus.Rejected;
	}

	@computedFrom('model.status')
	get rowStatusClass() {
		switch (this.model.status) {
			case ImportItemStatus.Warning:
			case ImportItemStatus.Error:
			case ImportItemStatus.Staged:
			case ImportItemStatus.Imported:
				return '';
			case ImportItemStatus.Rejected:
				return 'rejected-row';
			default:
				throw new Error(`Import item status ${this.model.status} is not valid.`);
		}
	}

	constructor(init?: Partial<ImportItem>) {
		Object.assign(this, init);

		if (this.model.mappingErrors == null) {
			this.model.mappingErrors = {};
		} else {
			this.accountErrors = (this.model.mappingErrors.AccountNumber || [])
				.concat(this.model.mappingErrors.SourceAccountId || []);
			this.costCenterErrors = (this.model.mappingErrors.CostCenterIdentifier || [])
				.concat(this.model.mappingErrors.SourceServiceId || []);
		}
	}

	async onReject() {
		this.reject();
		this.viewModel.onReject();

		try {
			const response = await this.viewModel.importService.setStatusForImportItem(
				this.model.id,
				ImportItemStatus.Rejected,
				this.viewModel.importEntry.type);

			if (!response.ok) {
				this.undo();
				this.viewModel.onUndo();
			}
		} catch (err) {
			this.undo();
			this.viewModel.onUndo();
		}
	}

	async onUndo() {
		const currentStatus = this.model.status;
		this.undo();
		this.viewModel.onUndo();

		const undoUndo = () => {
			this.changeItemStatusTo(currentStatus);
		};

		try {
			const response = await this.viewModel.importService.setStatusForImportItem(
				this.model.id,
				this.model.status,
				this.viewModel.importEntry.type);

			if (!response.ok) {
				undoUndo();
			}
		} catch (err) {
			undoUndo();
		}
	}

	reject() {
		this.previousStatus = this.model.status;
		this.changeItemStatusTo(ImportItemStatus.Rejected);
	}

	undo() {
		const inferPreviousStatus =
			(item: IImportItemAM): ImportItemStatus => {
				if (isEmpty(item.mappingErrors)) {
					return ImportItemStatus.Staged;
				}

				const mappingErrorsHaveErrorSeverities = keys(item.mappingErrors)
					.some(key => {
						const propErrors = item.mappingErrors[key];
						return propErrors != null
							? propErrors.some(error => error.severity === ImportMappingErrorSeverity.Error)
							: false;
					});

				const dimensionValuesHaveErrorSeverities = item.dimensionValues != null
					&& item.dimensionValues.some(value => value.mappingErrors != null
						&& value.mappingErrors.some(error => error.severity === ImportMappingErrorSeverity.Error));

				return mappingErrorsHaveErrorSeverities || dimensionValuesHaveErrorSeverities
					? ImportItemStatus.Error
					: ImportItemStatus.Warning;
			};

		this.changeItemStatusTo(this.previousStatus || inferPreviousStatus(this.model));
	}

	changeItemStatusTo(status: ImportItemStatus): void {

		this.viewModel.importEntry.statusCounts[<string>this.model.status] = this.viewModel.importEntry.statusCounts[<string>this.model.status] - 1;

		const toCount = this.viewModel.importEntry.statusCounts[<string>status];
		this.viewModel.importEntry.statusCounts[status] = toCount ? toCount + 1 : 1;

		this.model.status = status;
	}
}

export interface IImportItemList {
	importService: ImportService;
	importEntry: IImportAM;

	onReject(): void;
	onUndo(): void;
}
